import React, { memo } from "react";
import { Box } from "@mui/material";
import { Control, FieldValues, Path } from "react-hook-form";
import UploadImage from "components/image/UploadImage";
import resizeAndConvertToJPG from "util/resize_image";
import { UPLOAD_ACCEPT, UPLOAD_MAX_SIZE } from "constants/upload";
import { TUploadImageType } from "../types/wheel-brand";

interface MultipleImageUploadProps<T extends FieldValues> {
  baseFieldName: string;
  control: Control<T>;
  maxImages?: number;
  accept?: string;
  maxSize?: number;
  imageWidth?: string | number;
  imageHeight?: string | number;
  disabled?: boolean;
  itemCd?: string;
  color?: string;
  cacheBuster?: number; // Add cache buster prop
  onDeleteImage: (type: TUploadImageType, color?: string, hole?: string) => Promise<void>;
  onUploadImage: (
    type: TUploadImageType,
    file: File,
    color?: string,
    hole?: string,
  ) => Promise<void>;
}

const MultipleImageUpload = memo(
  <T extends FieldValues>({
    baseFieldName,
    control,
    maxImages = 3,
    accept = UPLOAD_ACCEPT.IMAGE.MIME_TYPES,
    maxSize = UPLOAD_MAX_SIZE.IMAGE,
    imageWidth = 190,
    imageHeight = 140,
    disabled = false,
    itemCd,
    color,
    cacheBuster,
    onDeleteImage,
    onUploadImage,
  }: MultipleImageUploadProps<T>) => {
    // Calculate hole number based on index (4, 5, 6 holes)
    const getHoleNumber = (index: number) => index + 4; // 0 -> 4 holes, 1 -> 5 holes, 2 -> 6 holes
    // Handle image upload with API call
    const handleImageUpload = async (file: File | null, index: number) => {
      if (file && itemCd && color) {
        try {
          const hole = getHoleNumber(index);
          const resizedImage = await resizeAndConvertToJPG(file);
          await onUploadImage("detail", resizedImage, color, String(hole));
          // await uploadBrandWheelImage(itemCd, resizedImage, "detail", color, String(hole));
        } catch (error) {
          console.error("Upload failed:", error);
        }
      }
    };

    // Handle image removal
    const handleRemoveImage = async (index: number) => {
      if (itemCd && color) {
        const hole = getHoleNumber(index);
        await onDeleteImage("detail", color, String(hole));
      }
    };

    return (
      <Box
        sx={{
          display: "flex",
          gap: { xs: 1, md: 2 },
          flexWrap: "wrap",
          justifyContent: "flex-start",
        }}
      >
        {Array.from({ length: maxImages }, (_, index) => (
          <Box
            key={index}
            sx={{
              flex: "0 0 auto",
              width: { xs: "calc((100% - 16px)/3)", md: "calc((100% - 32px)/3)" },
            }}
          >
            <UploadImage
              name={`${baseFieldName}_${index}` as Path<T>}
              control={control}
              accept={accept}
              maxSize={maxSize}
              width={imageWidth}
              height={imageHeight}
              disabled={disabled}
              sxBox={{ width: "100%" }}
              onImageChange={(file) => handleImageUpload(file, index)}
              onRemoveImage={() => handleRemoveImage(index)}
              cacheBuster={cacheBuster}
            />
          </Box>
        ))}
      </Box>
    );
  },
);

MultipleImageUpload.displayName = "MultipleImageUpload";

export default MultipleImageUpload;
